import type React from 'react';
import { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

//Utils
import { setFocusOnFormSubmit } from '../../utils/helpers';

// Components
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import { useAppSelector } from 'hooks';
import { selectIsAuthorIconVisible } from 'reducers/workflows';

interface IntentFormWrapperProps {
  /** Boolean to determine if wrapper should have a background color */
  background?: boolean;
  /** Element to display inside the form */
  children: React.ReactNode;
  className?: string;
  /** Boolean to determine if submit on enter should be disabled */
  disableSubmitOnEnter?: boolean;
  /** Title message for the form */
  formTitle: string;
  /** Boolean to determine if submit button is disabled */
  isDisabled: boolean;
  isForm: boolean;
  isLastStep: boolean;
  noValidate?: boolean;
  onSubmit?: (e: React.SyntheticEvent) => void;
  stepIndex: number;
}

const IntentFormWrapper: React.FC<
  React.PropsWithChildren<IntentFormWrapperProps>
> = ({
  background = true,
  children,
  className,
  disableSubmitOnEnter,
  formTitle,
  isDisabled,
  isForm,
  noValidate,
  onSubmit,
  stepIndex,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  useEffect(() => {
    return () => setFocusOnFormSubmit(formRef);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // let shift + enter propagate so new lines in text areas are supported
    if (e.key === 'Enter' && e.shiftKey) {
      return;
    }

    if (e.key === 'Enter' && !isDisabled && !disableSubmitOnEnter && onSubmit) {
      onSubmit(e);
      return;
    }
  };

  return isForm ? (
    <FormContainer
      background={background}
      className={className}
      data-testid='formWrapper'
      noValidate={noValidate}
      onKeyDown={handleKeyDown}
      onSubmit={(e: React.FormEvent) => e.preventDefault()}
      ref={formRef}
    >
      {formTitle && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {formTitle}
        </AgathaMessage>
      )}
      <div>{children}</div>
    </FormContainer>
  ) : (
    <Container className={className}>
      {formTitle && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {formTitle}
        </AgathaMessage>
      )}
      <div>{children}</div>
    </Container>
  );
};

export default IntentFormWrapper;

const FormContainer = styled('form')<{ background?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${props =>
    props.background ? props.theme.palette.background.default : 'transparent'};
  z-index: 9999; // prevent z-index items from workflow from overlapping
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.background.default};
  padding-top: 8px;
  z-index: 9999; // prevent z-index items from workflow from overlapping
`;
